import React, { useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Assessment as ComplianceIcon,
  AccountBalance as RegIcon,
  Security as AuditIcon,
  Warning as RiskIcon,
  TrendingUp as GrowthIcon,
  
  Schedule as PendingIcon,
  Gavel as LegalIcon,
} from "@mui/icons-material";
import Chart from "chart.js/auto";
import Layout from "../components/layout/CompliceLayout";

const Compliances = () => {
  const theme = useTheme();
  const complianceChartRef = useRef(null);
  const riskChartRef = useRef(null);
  const performanceChartRef = useRef(null);
  const complianceChartInstanceRef = useRef(null);
  const riskChartInstanceRef = useRef(null);
  const performanceChartInstanceRef = useRef(null);

  useEffect(() => {
    // Destroy existing charts
    if (complianceChartInstanceRef.current) {
      complianceChartInstanceRef.current.destroy();
    }
    if (riskChartInstanceRef.current) {
      riskChartInstanceRef.current.destroy();
    }
    if (performanceChartInstanceRef.current) {
      performanceChartInstanceRef.current.destroy();
    }

    // Compliance Trend Chart
    const complianceCtx = complianceChartRef.current.getContext("2d");
    complianceChartInstanceRef.current = new Chart(complianceCtx, {
      type: "line",
      data: {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
        datasets: [
          {
            label: "Compliance Score",
            data: [85, 92, 88, 95, 90, 97],
            borderColor: theme.palette.success.main,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: theme.palette.success.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
            pointRadius: 6,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            grid: {
              color: alpha(theme.palette.text.secondary, 0.1),
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
              callback: function(value) {
                return value + '%';
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
            },
          },
        },
      },
    });

    // Risk Assessment Chart
    const riskCtx = riskChartRef.current.getContext("2d");
    riskChartInstanceRef.current = new Chart(riskCtx, {
      type: "doughnut",
      data: {
        labels: ["Low Risk", "Medium Risk", "High Risk"],
        datasets: [
          {
            data: [65, 25, 10],
            backgroundColor: [
              theme.palette.success.main,
              theme.palette.warning.main,
              theme.palette.error.main,
            ],
            borderWidth: 0,
            cutout: '70%',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              color: theme.palette.text.primary,
              font: {
                family: theme.typography.fontFamily,
                size: 10,
              },
              padding: 8,
              boxWidth: 8,
              boxHeight: 8,
            },
          },
        },
      },
    });

    // Performance Chart
    const performanceCtx = performanceChartRef.current.getContext("2d");
    performanceChartInstanceRef.current = new Chart(performanceCtx, {
      type: "bar",
      data: {
        labels: ["Regulatory", "Tax", "Legal", "Audit", "ESG", "Data"],
        datasets: [
          {
            label: "Compliance Score",
            data: [95, 88, 92, 85, 78, 90],
            backgroundColor: [
              theme.palette.primary.main,
              theme.palette.info.main,
              theme.palette.success.main,
              theme.palette.warning.main,
              theme.palette.error.main,
              theme.palette.secondary.main,
            ],
            borderRadius: 8,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            grid: {
              color: alpha(theme.palette.text.secondary, 0.1),
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
              callback: function(value) {
                return value + '%';
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
            },
          },
        },
      },
    });

    return () => {
      if (complianceChartInstanceRef.current) {
        complianceChartInstanceRef.current.destroy();
      }
      if (riskChartInstanceRef.current) {
        riskChartInstanceRef.current.destroy();
      }
      if (performanceChartInstanceRef.current) {
        performanceChartInstanceRef.current.destroy();
      }
    };
  }, [theme]);

  const MetricCard = ({ icon, title, value, change, color, trend }) => (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "visible",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 56,
              height: 56,
              border: `2px solid ${alpha(color, 0.2)}`,
            }}
          >
            {icon}
          </Avatar>
          {change && (
            <Chip
              icon={trend === "up" ? <GrowthIcon /> : null}
              label={change}
              size="small"
              sx={{
                bgcolor: trend === "up" ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                color: trend === "up" ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600,
                border: "none",
              }}
            />
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const ComplianceProgressCard = ({ title, progress, color, subtitle, icon }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 40,
              height: 40,
              mr: 2,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: color }}
          >
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha(color, 0.1),
            "& .MuiLinearProgress-bar": {
              backgroundColor: color,
              borderRadius: 4,
            },
          }}
        />
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, subtitle, children }) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: 350,
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box 
        sx={{ 
          flex: 1, 
          position: "relative",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Paper>
  );

  return (
    <Layout>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Compliance Portal Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Monitor your compliance status and regulatory requirements across all business areas.
          </Typography>
        </Box>

        {/* Key Metrics Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<ComplianceIcon sx={{ fontSize: 28 }} />}
              title="Overall Compliance Score"
              value="92%"
              change="+5.2%"
              color={theme.palette.success.main}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<RiskIcon sx={{ fontSize: 28 }} />}
              title="Risk Assessment"
              value="Low"
              change="-12%"
              color={theme.palette.info.main}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<RegIcon sx={{ fontSize: 28 }} />}
              title="Active Regulations"
              value="24"
              change="+3"
              color={theme.palette.warning.main}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<AuditIcon sx={{ fontSize: 28 }} />}
              title="Audit Readiness"
              value="85%"
              change="+8%"
              color={theme.palette.primary.main}
              trend="up"
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Compliance Trend"
              subtitle="Monthly compliance score evolution"
            >
              <canvas ref={complianceChartRef} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Risk Distribution"
              subtitle="Current risk assessment breakdown"
            >
              <canvas ref={riskChartRef} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Compliance by Category"
              subtitle="Performance across different areas"
            >
              <canvas ref={performanceChartRef} />
            </ChartCard>
          </Grid>
        </Grid>

        {/* Compliance Progress Section */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Compliance Areas
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Track compliance status across key regulatory areas
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ComplianceProgressCard
              title="Regulatory Compliance"
              subtitle="Government & industry regulations"
              progress={95}
              color={theme.palette.success.main}
              icon={<RegIcon />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ComplianceProgressCard
              title="Data Protection"
              subtitle="GDPR, privacy & security standards"
              progress={78}
              color={theme.palette.warning.main}
              icon={<PendingIcon />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ComplianceProgressCard
              title="Legal Framework"
              subtitle="Contractual & legal obligations"
              progress={88}
              color={theme.palette.info.main}
              icon={<LegalIcon />}
            />
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default Compliances;
